import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getTags, deleteTag, bulkDeleteTags } from '../../../services/tagService';
import { getCategories } from '../../../services/categoryService';
import { getSubcategories } from '../../../services/subcategoryService';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiFilter, FiDownload } from 'react-icons/fi';
import { exportToCSV, tagColumns } from '../../../utils/csvExport';

const TagsPage = () => {
    const [tags, setTags] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tagToDelete, setTagToDelete] = useState(null);
    const [filters, setFilters] = useState({
        categoryId: '',
        subcategoryId: ''
    });
    const [selectedTags, setSelectedTags] = useState([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        fetchCategories();
        fetchTags();
    }, []);

    useEffect(() => {
        if (filters.categoryId) {
            fetchSubcategories(filters.categoryId);
        } else {
            setSubcategories([]);
            setFilters(prev => ({ ...prev, subcategoryId: '' }));
        }
    }, [filters.categoryId]);

    useEffect(() => {
        fetchTags(filters);
    }, [filters.categoryId, filters.subcategoryId]);

    const fetchCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await getSubcategories(categoryId);
            setSubcategories(response.data || []);
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            toast.error('Failed to load subcategories');
        }
    };

    const fetchTags = async (params = {}) => {
        try {
            setLoading(true);
            const response = await getTags(params);
            setTags(response.data || []);
            // Reset selections when fetching new data
            setSelectedTags([]);
            setSelectAll(false);
        } catch (error) {
            console.error('Error fetching tags:', error);
            toast.error('Failed to load tags');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (tag) => {
        setTagToDelete(tag);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!tagToDelete) return;

        try {
            await deleteTag(tagToDelete._id);
            toast.success(`Tag "${tagToDelete.name}" deleted successfully`);
            setTags(tags.filter(t => t._id !== tagToDelete._id));
        } catch (error) {
            console.error('Error deleting tag:', error);
            toast.error(error.response?.data?.message || 'Failed to delete tag');
        } finally {
            setShowDeleteModal(false);
            setTagToDelete(null);
        }
    };

    const handleCheckboxChange = (tagId) => {
        setSelectedTags(prev => {
            if (prev.includes(tagId)) {
                return prev.filter(id => id !== tagId);
            } else {
                return [...prev, tagId];
            }
        });
    };

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedTags([]);
        } else {
            setSelectedTags(tags.map(tag => tag._id));
        }
        setSelectAll(!selectAll);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            categoryId: '',
            subcategoryId: ''
        });
    };

    const handleBulkDeleteClick = () => {
        if (selectedTags.length === 0) {
            toast.info('No tags selected');
            return;
        }
        setShowBulkDeleteModal(true);
    };

    const handleBulkDeleteConfirm = async () => {
        if (selectedTags.length === 0) return;

        try {
            // Use the new bulkDeleteTags function instead of individual deleteTag calls
            const result = await bulkDeleteTags(selectedTags);

            // Show appropriate messages based on the response
            if (result.notFound && result.notFound.length > 0) {
                toast.warning(`${result.notFound.length} tags were not found`);
            }

            if (result.deletedCount > 0) {
                toast.success(`${result.deletedCount} tags deleted successfully`);
            } else {
                toast.error('No tags were deleted');
            }

            // Refresh the list
            fetchTags(filters);
        } catch (error) {
            console.error('Error in bulk delete operation:', error);
            toast.error(error.response?.data?.message || 'Failed to complete bulk delete operation');
        } finally {
            setShowBulkDeleteModal(false);
            setSelectedTags([]);
            setSelectAll(false);
        }
    };

    const handleExportCSV = () => {
        if (tags.length === 0) {
            toast.info('No tags to export');
            return;
        }

        try {
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `tags_export_${timestamp}.csv`;
            exportToCSV(tags, tagColumns, filename);
            toast.success('Tags exported successfully');
        } catch (error) {
            console.error('Error exporting tags:', error);
            toast.error('Failed to export tags');
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Tags</h1>
                <div className="flex space-x-2">
                    {selectedTags.length > 0 && (
                        <button
                            onClick={handleBulkDeleteClick}
                            className="btn btn-outline border-red-300 text-red-600 hover:bg-red-50 flex items-center"
                        >
                            <FiTrash2 className="mr-2" /> Delete Selected ({selectedTags.length})
                        </button>
                    )}
                    <button
                        onClick={handleExportCSV}
                        className="btn btn-outline border-green-300 text-green-600 hover:bg-green-50 flex items-center"
                    >
                        <FiDownload className="mr-2" /> Export CSV
                    </button>
                    <Link to="/admin/tags/new" className="btn btn-primary flex items-center">
                        <FiPlus className="mr-2" /> Add Tag
                    </Link>
                </div>
            </div>

            <div className="bg-[#f8f8f8] shadow-md rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Category
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiFilter className="h-5 w-5 text-gray-400" />
                            </div>
                            <select
                                name="categoryId"
                                value={filters.categoryId}
                                onChange={handleFilterChange}
                                className="input pl-10 pr-10 py-2"
                            >
                                <option value="">All Categories</option>
                                {categories.map(category => (
                                    <option key={category._id} value={category._id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Subcategory
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiFilter className="h-5 w-5 text-gray-400" />
                            </div>
                            <select
                                name="subcategoryId"
                                value={filters.subcategoryId}
                                onChange={handleFilterChange}
                                className="input pl-10 pr-10 py-2"
                                disabled={!filters.categoryId}
                            >
                                <option value="">All Subcategories</option>
                                {subcategories.map(subcategory => (
                                    <option key={subcategory._id} value={subcategory._id}>
                                        {subcategory.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={clearFilters}
                            className="btn btn-outline py-2"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden">
                    {tags.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            No tags found. Click the "Add Tag" button to create one.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-3 text-left">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                checked={selectAll}
                                                onChange={handleSelectAllChange}
                                            />
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subcategory
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                {tags.map((tag) => (
                                    <tr key={tag._id} className={`hover:bg-gray-50 ${selectedTags.includes(tag._id) ? 'bg-blue-50' : ''}`}>
                                        <td className="px-3 py-4">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                    checked={selectedTags.includes(tag._id)}
                                                    onChange={() => handleCheckboxChange(tag._id)}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{tag.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {tag.category?.name || 'All Categories'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {tag.subcategory?.name || 'All Subcategories'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${tag.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}>
                                                {tag.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-3">
                                                <Link
                                                    to={`/admin/tags/${tag._id}/view`}
                                                    className="text-gray-600 hover:text-primary-600"
                                                >
                                                    <FiEye size={18} />
                                                </Link>
                                                <Link
                                                    to={`/admin/tags/${tag._id}/edit`}
                                                    className="text-gray-600 hover:text-primary-600"
                                                >
                                                    <FiEdit size={18} />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteClick(tag)}
                                                    className="text-gray-600 hover:text-primary-600"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Single Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Tag</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete the tag "{tagToDelete?.name}"? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleDeleteConfirm}
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Selected Tags</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete {selectedTags.length} selected tags? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleBulkDeleteConfirm}
                                >
                                    Delete All Selected
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowBulkDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default TagsPage;